import { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useNotificationsWS } from '../hooks/useNotificationsWS';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import {
  HardHat, ArrowLeft, Send, Paperclip, Phone, Mail, MapPin,
  CheckCircle, Clock, AlertCircle, User, Shield, Star,
  DollarSign, Calendar, X, ThumbsUp, ThumbsDown
} from 'lucide-react';
import LanguageSwitcher from '../components/LanguageSwitcher';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const MessagesPage = () => {
  const { jobId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [job, setJob] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [otherUser, setOtherUser] = useState(null);
  const [contactRequest, setContactRequest] = useState(null);
  const [contactRevealed, setContactRevealed] = useState(false);
  const [revealedContact, setRevealedContact] = useState(null);
  const [requestingContact, setRequestingContact] = useState(false);
  const [processingRequest, setProcessingRequest] = useState(false);
  const messagesEndRef = useRef(null);

  useEffect(() => {
    fetchJobAndMessages();
    // Poll for new messages every 10 seconds as fallback (WS handles real-time)
    const interval = setInterval(fetchMessages, 10000);
    return () => clearInterval(interval);
  }, [jobId]);

  // Real-time updates via WebSocket
  const handleWSEvent = useCallback((event) => {
    if (!event) return;
    if (event.type === 'new_message' && event.job_id === jobId) {
      fetchMessages();
    } else if (event.type === 'contact_approved' && event.job_id === jobId) {
      fetchJobAndMessages();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jobId]);
  useNotificationsWS(handleWSEvent);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const fetchJobAndMessages = async () => {
    try {
      const [jobRes, messagesRes] = await Promise.all([
        axios.get(`${API}/jobs/${jobId}`),
        axios.get(`${API}/messages/${jobId}`)
      ]);
      
      setJob(jobRes.data);
      setMessages(messagesRes.data);
      setContactRevealed(jobRes.data.contact_revealed);
      
      // Determine the other user
      if (user.role === 'client') {
        // Get contractor info
        if (jobRes.data.selected_contractor_id) {
          const contractorRes = await axios.get(`${API}/contractors/${jobRes.data.selected_contractor_id}/public`).catch(() => null);
          if (contractorRes) {
            setOtherUser(contractorRes.data);
          }
        }
      } else {
        // Contractor viewing - get client info if contact revealed
        if (jobRes.data.contact_revealed) {
          // Client info would be in the contact reveal response
        }
      }
      
      // Check for pending contact requests
      await fetchContactRequest();
      
    } catch (error) {
      console.error('Failed to fetch data:', error);
      if (error.response?.status === 403) {
        navigate(-1);
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchMessages = async () => {
    try {
      const response = await axios.get(`${API}/messages/${jobId}`);
      setMessages(response.data);
    } catch (error) {
      console.error('Failed to fetch messages:', error);
    }
  };

  const fetchContactRequest = async () => {
    try {
      const response = await axios.get(`${API}/contact-request/job/${jobId}`);
      setContactRequest(response.data);
      if (response.data?.status === 'approved' && response.data?.revealed_contact) {
        setRevealedContact(response.data.revealed_contact);
        setContactRevealed(true);
      }
    } catch (error) {
      // No contact request exists yet
    }
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!newMessage.trim() || sending) return;

    setSending(true);
    try {
      const receiverId = user.role === 'client' 
        ? job.selected_contractor_id 
        : job.client_id;

      await axios.post(`${API}/messages`, {
        job_id: jobId,
        receiver_id: receiverId,
        content: newMessage.trim(),
        attachments: []
      });

      setNewMessage('');
      fetchMessages();
    } catch (error) {
      console.error('Failed to send message:', error);
      alert(error.response?.data?.detail || 'Failed to send message');
    } finally {
      setSending(false);
    }
  };

  const handleRequestContact = async () => {
    setRequestingContact(true);
    try {
      await axios.post(`${API}/contact-request/${jobId}`);
      await fetchContactRequest();
      alert('Contact request sent! The client will be notified.');
    } catch (error) {
      console.error('Failed to request contact:', error);
      alert(error.response?.data?.detail || 'Failed to send request');
    } finally {
      setRequestingContact(false);
    }
  };

  const handleApproveContact = async () => {
    if (!contactRequest) return;
    
    setProcessingRequest(true);
    try {
      const response = await axios.post(`${API}/contact-request/${contactRequest.id}/approve`);
      setRevealedContact(response.data.client_info);
      setContactRevealed(true);
      await fetchContactRequest();
      alert('Contact information has been shared with the contractor.');
    } catch (error) {
      console.error('Failed to approve contact:', error);
      alert(error.response?.data?.detail || 'Failed to approve request');
    } finally {
      setProcessingRequest(false);
    }
  };

  const handleRejectContact = async () => {
    if (!contactRequest) return;
    
    setProcessingRequest(true);
    try {
      await axios.post(`${API}/contact-request/${contactRequest.id}/reject`);
      await fetchContactRequest();
    } catch (error) {
      console.error('Failed to reject contact:', error);
    } finally {
      setProcessingRequest(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-100 flex items-center justify-center">
        <div className="animate-spin w-8 h-8 border-4 border-orange-600 border-t-transparent rounded-full" />
      </div>
    );
  }

  if (!job) {
    return (
      <div className="min-h-screen bg-slate-100 flex items-center justify-center">
        <p className="text-slate-500">Job not found</p>
      </div>
    );
  }

  const isClient = user.role === 'client';
  const canMessage = job.selected_contractor_id && (isClient || job.selected_contractor_id === user.id);

  return (
    <div className="min-h-screen bg-slate-100 flex flex-col">
      {/* Header */}
      <header className="bg-slate-900 sticky top-0 z-50">
        <div className="max-w-4xl mx-auto px-4 md:px-8">
          <div className="flex items-center justify-between h-16">
            <button 
              onClick={() => navigate(-1)}
              className="flex items-center gap-2 text-slate-400 hover:text-white"
            >
              <ArrowLeft className="w-5 h-5" />
              <span className="text-sm font-medium">{t('common.back')}</span>
            </button>
            <div className="text-center">
              <h1 className="text-white font-bold truncate max-w-[200px]">{job.title}</h1>
              <p className="text-slate-400 text-xs">Job #{jobId.slice(0, 8)}</p>
            </div>
            <div className="flex items-center gap-2">
              <LanguageSwitcher variant="dark" />
            </div>
          </div>
        </div>
      </header>

      <div className="flex-1 flex flex-col max-w-4xl mx-auto w-full">
        {/* Contact Request Banner for Client */}
        {isClient && contactRequest?.status === 'pending' && (
          <div className="bg-orange-50 border-b border-orange-200 p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-orange-100 rounded-full flex items-center justify-center">
                  <Phone className="w-5 h-5 text-orange-600" />
                </div>
                <div>
                  <p className="font-bold text-orange-800">Contact Request</p>
                  <p className="text-sm text-orange-700">
                    The contractor wants to share contact information. Charge: ${contactRequest.charge_amount}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={handleApproveContact}
                  disabled={processingRequest}
                  className="bg-green-600 text-white px-4 py-2 rounded-sm font-bold text-sm flex items-center gap-1 disabled:opacity-50"
                  data-testid="approve-contact-btn"
                >
                  <ThumbsUp className="w-4 h-4" /> Approve
                </button>
                <button
                  onClick={handleRejectContact}
                  disabled={processingRequest}
                  className="bg-slate-200 text-slate-700 px-4 py-2 rounded-sm font-bold text-sm flex items-center gap-1 disabled:opacity-50"
                  data-testid="reject-contact-btn"
                >
                  <ThumbsDown className="w-4 h-4" /> Reject
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Contact Revealed Banner */}
        {contactRevealed && (
          <div className="bg-green-50 border-b border-green-200 p-4">
            <div className="flex items-center gap-3">
              <CheckCircle className="w-6 h-6 text-green-600" />
              <div className="flex-1">
                <p className="font-bold text-green-800">Contact Information Shared</p>
                {revealedContact && (
                  <div className="flex items-center gap-4 mt-1 text-sm text-green-700">
                    <span className="flex items-center gap-1">
                      <User className="w-4 h-4" /> {revealedContact.name}
                    </span>
                    {revealedContact.email && (
                      <span className="flex items-center gap-1">
                        <Mail className="w-4 h-4" /> {revealedContact.email}
                      </span>
                    )}
                    {revealedContact.phone && (
                      <span className="flex items-center gap-1">
                        <Phone className="w-4 h-4" /> {revealedContact.phone}
                      </span>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Request Contact Button for Contractor */}
        {!isClient && job.selected_contractor_id === user.id && !contactRevealed && !contactRequest && (
          <div className="bg-blue-50 border-b border-blue-200 p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="font-bold text-blue-800">Ready to connect?</p>
                <p className="text-sm text-blue-700">
                  Request contact info to take this project forward. You'll be charged ${job.lead_price} when approved.
                </p>
              </div>
              <button
                onClick={handleRequestContact}
                disabled={requestingContact}
                className="bg-orange-600 text-white px-4 py-2 rounded-sm font-bold text-sm flex items-center gap-2 disabled:opacity-50"
                data-testid="request-contact-btn"
              >
                {requestingContact ? 'Requesting...' : (
                  <>
                    <Phone className="w-4 h-4" /> Request Contact Info
                  </>
                )}
              </button>
            </div>
          </div>
        )}

        {/* Pending Request Status for Contractor */}
        {!isClient && contactRequest?.status === 'pending' && (
          <div className="bg-yellow-50 border-b border-yellow-200 p-4">
            <div className="flex items-center gap-3">
              <Clock className="w-5 h-5 text-yellow-600" />
              <p className="text-yellow-800">
                <strong>Contact request pending.</strong> Waiting for client approval.
              </p>
            </div>
          </div>
        )}

        {/* Messages Area */}
        <div className="flex-1 overflow-y-auto p-4 space-y-4">
          {messages.length === 0 ? (
            <div className="text-center py-12">
              <div className="w-16 h-16 bg-slate-200 rounded-full flex items-center justify-center mx-auto mb-4">
                <Send className="w-8 h-8 text-slate-400" />
              </div>
              <p className="text-slate-500 font-medium">No messages yet</p>
              <p className="text-sm text-slate-400 mt-1">
                Start the conversation to discuss the project
              </p>
            </div>
          ) : (
            messages.map((message) => {
              const isOwnMessage = message.sender_id === user.id;
              return (
                <div
                  key={message.id}
                  className={`flex ${isOwnMessage ? 'justify-end' : 'justify-start'}`}
                >
                  <div
                    className={`max-w-[70%] rounded-lg p-3 ${
                      isOwnMessage
                        ? 'bg-orange-600 text-white'
                        : 'bg-white border border-slate-200 text-slate-800'
                    }`}
                  >
                    <p className="whitespace-pre-wrap">{message.content}</p>
                    <p className={`text-xs mt-1 ${isOwnMessage ? 'text-orange-200' : 'text-slate-400'}`}>
                      {new Date(message.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                    </p>
                  </div>
                </div>
              );
            })
          )}
          <div ref={messagesEndRef} />
        </div>

        {/* Message Input */}
        {canMessage ? (
          <div className="bg-white border-t border-slate-200 p-4">
            <form onSubmit={handleSendMessage} className="flex items-center gap-3">
              <input
                type="text"
                value={newMessage}
                onChange={(e) => setNewMessage(e.target.value)}
                placeholder="Type your message..."
                className="flex-1 px-4 py-3 border border-slate-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-orange-600"
                data-testid="message-input"
              />
              <button
                type="submit"
                disabled={!newMessage.trim() || sending}
                className="bg-orange-600 hover:bg-orange-700 text-white p-3 rounded-sm disabled:opacity-50 disabled:cursor-not-allowed"
                data-testid="send-message-btn"
              >
                <Send className="w-5 h-5" />
              </button>
            </form>
          </div>
        ) : (
          <div className="bg-slate-100 border-t border-slate-200 p-4 text-center">
            <p className="text-slate-500 text-sm">
              {!job.selected_contractor_id 
                ? 'Select a contractor to start messaging' 
                : 'You cannot message on this job'}
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default MessagesPage;
